import React, { useState, useEffect } from 'react'
import { RiCollapseDiagonalLine } from "react-icons/ri";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { FaFilter } from "react-icons/fa";
import { TbReload, TbArrowsSort } from "react-icons/tb";

const API_URL = process.env.REACT_APP_API_URL || "https://wa-api.globebot.io";

// ─── Token helper ─────────────────────────────────────────────────────────────
function getToken() {
    if (typeof window !== 'undefined' && typeof window.locatorgarsotrga === 'function') {
        return window.locatorgarsotrga("token") || null;
    }
    return typeof window !== 'undefined' ? localStorage.getItem('token') : null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatDate(dt) {
    if (!dt) return '—';
    try {
        const d = new Date(dt);
        return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch { return dt; }
}

function StatusBadge({ status }) {
    const map = {
        completed: 'bg-[#d1fae5] text-[#065f46]',
        partial:   'bg-[#fffbe6] text-[#856404]',
        running:   'bg-[#e0f2fe] text-[#0369a1]',
        failed:    'bg-[#fee2e2] text-[#991b1b]',
    };
    return (
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${map[status] || 'bg-gray-100 text-gray-500'}`}>
            {status || '—'}
        </span>
    );
}

function MiniProgress({ sent, failed, total }) {
    if (!total) return <span className='text-[11px] text-gray-400'>—</span>;
    const sentPct   = Math.round((sent   / total) * 100);
    const failedPct = Math.round((failed / total) * 100);
    return (
        <div className='min-w-[100px]'>
            <div className='w-full h-[6px] bg-[#e5e7eb] rounded-full overflow-hidden flex'>
                <div className='h-full bg-[#ed7b00]' style={{ width: `${sentPct}%` }} />
                <div className='h-full bg-[#ed5050]' style={{ width: `${failedPct}%` }} />
            </div>
            <div className='flex justify-between text-[10px] mt-0.5'>
                <span className='text-[#ed7b00] font-semibold'>✓ {sent}</span>
                <span className='text-[#ed5050] font-semibold'>✗ {failed}</span>
                <span className='text-gray-400'>{total}</span>
            </div>
        </div>
    );
}

// ─── Detail Drawer ────────────────────────────────────────────────────────────
function CampaignDrawer({ campaign, token, onClose }) {
    const [messages, setMessages] = useState([]);
    const [loading,  setLoading]  = useState(true);
    const [error,    setError]    = useState(null);
    const [page,     setPage]     = useState(1);
    const PAGE_SIZE = 10;

    useEffect(() => {
        if (!campaign?.id) return;
        setLoading(true);
        fetch(`${API_URL}/auth/campaign/${campaign.id}`, {
            headers: { Authorization: `Bearer ${token || ''}` }
        })
            .then(r => r.json())
            .then(data => { setMessages(Array.isArray(data.data) ? data.data : []); setLoading(false); })
            .catch(() => { setError('Failed to load campaign messages.'); setLoading(false); });
    }, [campaign?.id]);

    const totalPages  = Math.ceil(messages.length / PAGE_SIZE);
    const paged       = messages.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const sentCount   = messages.filter(m => m.status === 'sent' || m.status === 'delivered').length;
    const failedCount = messages.filter(m => m.status === 'failed').length;
    const uniqueMessages = Object.values(
        messages.reduce((acc, msg) => {
          // use phone/number/message_id as unique key
          const key = msg.number;
      
          // keep latest status
          acc[key] = msg;
      
          return acc;
        }, {})
      );
      
      const unisentCount = uniqueMessages.filter(
        m => m.status === 'sent'
      ).length;
      
      const unifailedCount = uniqueMessages.filter(
        m => m.status === 'failed'
      ).length;
    return (
        <div className='fixed inset-0 z-50 flex'>
            <div className='flex-1 bg-black bg-opacity-40' onClick={onClose} />
            <div className='w-full max-w-[600px] bg-white h-full overflow-y-auto shadow-2xl flex flex-col'>
                {/* Header */}
                <div className='flex items-center justify-between px-5 py-4 border-b border-[#e5e7eb] bg-[#f9fafb]'>
                    <div>
                        <h2 className='text-[15px] font-bold text-[#121519]'>
                            📊 {campaign.campaign_name || `Campaign #${campaign.id}`}
                        </h2>
                        <p className='text-[11px] text-gray-500 mt-0.5'>
                            Template: <strong>{campaign.template_name}</strong> · {campaign.language}
                        </p>
                    </div>
                    <button onClick={onClose} className='text-gray-400 hover:text-gray-700 text-[20px] font-bold'>✕</button>
                </div>

                {/* Stats */}
                <div className='grid grid-cols-3 gap-3 px-5 py-4 border-b border-[#e5e7eb]'>
                    {[
                        { label: 'Total',  value: uniqueMessages.length, color: 'text-[#374151]' },
                        { label: 'Sent',   value: unisentCount,            color: 'text-[#ed7b00]' },
                        { label: 'Failed', value: unifailedCount,          color: 'text-[#ed5050]' },
                    ].map((s, i) => (
                        <div key={i} className='bg-[#f9fafb] border border-[#e5e7eb] rounded-lg p-3 text-center'>
                            <p className={`text-[22px] font-bold ${s.color}`}>{s.value ?? '—'}</p>
                            <p className='text-[11px] text-gray-500'>{s.label}</p>
                        </div>
                    ))}
               
                </div>

                {/* Message log */}
                <div className='flex-1 px-5 py-4'>
                    <p className='text-[12px] font-bold text-[#374151] mb-3'>
                        Delivery Log ({messages.length} recipients)
                    </p>
                    {loading ? (
                        <div className='flex justify-center items-center py-10 gap-2'>
                            <div className='animate-spin w-5 h-5 border-4 border-[#ed7b00] border-t-transparent rounded-full'></div>
                            <span className='text-[13px] text-gray-500'>Loading...</span>
                        </div>
                    ) : error ? (
                        <p className='text-[13px] text-red-500'>{error}</p>
                    ) : messages.length === 0 ? (
                        <p className='text-[13px] text-gray-400 text-center py-8'>No message logs found.</p>
                    ) : (
                        <>
                            <table className='w-full border border-[#e5e7eb] text-[11px] rounded overflow-hidden'>
                                <thead className='bg-[#f3f4f6]'>
                                    <tr>
                                        {['#', 'Number', 'Status', 'Message ID', 'Error', 'Sent At'].map(h => (
                                            <th key={h} className='p-2 text-left text-[#576071] border-b border-[#e5e7eb] font-semibold'>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {paged.map((msg, i) => (
                                        <tr key={msg.id || i} className='border-b border-[#e5e7eb] hover:bg-[#f9fafb]'>
                                            <td className='p-2 text-gray-400'>{(page - 1) * PAGE_SIZE + i + 1}</td>
                                            <td className='p-2 font-mono font-semibold text-[#374151]'>{msg.phone_number}</td>
                                            <td className='p-2'><StatusBadge status={msg.status} /></td>
                                            <td className='p-2 text-gray-400 truncate max-w-[80px]' title={msg.message_id}>
                                                {msg.message_id ? msg.message_id.slice(0, 10) + '…' : '—'}
                                            </td>
                                            <td className='p-2 text-[#ed5050] max-w-[100px]' title={msg.error}>
                                                {msg.error || '—'}
                                            </td>
                                            <td className='p-2 text-gray-400 whitespace-nowrap'>{formatDate(msg.sent_at || msg.created_at)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {totalPages > 1 && (
                                <div className='flex items-center justify-end gap-2 mt-3'>
                                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                                        className='px-2 py-1 border rounded text-[11px] text-[#316bb0] border-[#316bb0] disabled:opacity-40'>‹ Prev</button>
                                    <span className='text-[11px] text-gray-500'>Page {page} of {totalPages}</span>
                                    <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                                        className='px-2 py-1 border rounded text-[11px] text-[#316bb0] border-[#316bb0] disabled:opacity-40'>Next ›</button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
function WhatsAppCampaignReport() {
    const token = getToken();

    // ── Data state ────────────────────────────────────────────────────────
    const [campaigns,        setCampaigns]        = useState([]);
    const [loading,          setLoading]          = useState(true);
    const [error,            setError]            = useState(null);
    const [selectedCampaign, setSelectedCampaign] = useState(null);

    // ── Filter state ──────────────────────────────────────────────────────
    const [showfilter,   setshowfilter]   = useState(false);
    const [Campaignname, setCampaignname] = useState('');
    const [TemplateName, setTemplateName] = useState('');
    const [TemplateType, setTemplateType] = useState('');
    const [Interface,    setInterface]    = useState('');
    const [IsScheduled,  setIsScheduled]  = useState('');

    // applied filters (only updated on Filter button click)
    const [applied, setApplied] = useState({
        campaignName: '', templateName: '', templateType: '', isScheduled: ''
    });

    // ── Pagination ────────────────────────────────────────────────────────
    const [currentPage,   setcurrentPage]   = useState(1);
    const [usersperpage]                    = useState(10);

    // ── Fetch ─────────────────────────────────────────────────────────────
    const fetchCampaigns = () => {
        setLoading(true);
        setError(null);
        fetch(`${API_URL}/auth/campaign/report`, {
            headers: { Authorization: `Bearer ${token || ''}` }
        })
            .then(r => r.json())
            .then(data => {
                setCampaigns(Array.isArray(data.data) ? data.data : []);
                setLoading(false);
            })
            .catch(() => { setError('Failed to load campaigns.'); setLoading(false); });
    };

    useEffect(() => { fetchCampaigns(); }, []); // eslint-disable-line

    // ── Apply filters ─────────────────────────────────────────────────────
    const applyFilters = () => {
        setApplied({
            campaignName: Campaignname,
            templateName: TemplateName,
            templateType: TemplateType,
            isScheduled:  IsScheduled,
        });
        setcurrentPage(1);
    };

    const clearFilters = () => {
        setCampaignname(''); setTemplateName(''); setTemplateType('');
        setInterface(''); setIsScheduled('');
        setApplied({ campaignName: '', templateName: '', templateType: '', isScheduled: '' });
        setcurrentPage(1);
    };

    // ── Filtered data ─────────────────────────────────────────────────────
    const filtered = campaigns.filter(c => {
        const nameMatch     = !applied.campaignName || (c.campaign_name  || '').toLowerCase().includes(applied.campaignName.toLowerCase());
        const tplMatch      = !applied.templateName || (c.template_name  || '').toLowerCase().includes(applied.templateName.toLowerCase());
        const scheduleMatch = !applied.isScheduled  || (applied.isScheduled === 'true' ? !!c.scheduled_at : !c.scheduled_at);
        return nameMatch && tplMatch && scheduleMatch;
    });

    // ── Pagination math ───────────────────────────────────────────────────
    const indexoflastuser  = currentPage * usersperpage;
    const indexoffirstuser = indexoflastuser - usersperpage;
    const pagedCampaigns   = filtered.slice(indexoffirstuser, indexoflastuser);
    const totalpages       = Math.max(1, Math.ceil(filtered.length / usersperpage));

    // ── Summary totals ────────────────────────────────────────────────────
    const totalSent      = campaigns.reduce((s, c) => s + (c.sent_count    || 0), 0);
    const totalFailed    = campaigns.reduce((s, c) => s + ((c.failed_count/3)  || 0), 0);
    const totalRecipients= campaigns.reduce((s, c) => s + (c.total_numbers || 0), 0);

    return (
        <>
            {/* Drawer */}
            {selectedCampaign && (
                <CampaignDrawer
                    campaign={selectedCampaign}
                    token={token}
                    onClose={() => setSelectedCampaign(null)}
                />
            )}

            {/* ── Summary cards ── */}
            <div className='grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4'>
                {[
                    { label: 'Total Campaigns',  value: campaigns.length, color: 'text-[#374151]', bg: 'bg-[#f9fafb]'  },
                    { label: 'Total Recipients', value: totalRecipients,  color: 'text-[#0369a1]', bg: 'bg-[#e0f2fe]'  },
                    { label: 'Total Sent',       value: totalSent,        color: 'text-[#ed7b00]', bg: 'bg-[#d1fae5]'  },
                    { label: 'Total Failed',     value: totalFailed,      color: 'text-[#ed5050]', bg: 'bg-[#fee2e2]'  },
                ].map((s, i) => (
                    <div key={i} className={`${s.bg} border border-[#e5e7eb] rounded-lg p-3 text-center`}>
                        <p className={`text-[22px] font-bold ${s.color}`}>{s.value}</p>
                        <p className='text-[11px] text-gray-500'>{s.label}</p>
                    </div>
                ))}
            </div>

            {/* ── Filter panel ── */}
            <div onClick={() => setshowfilter(!showfilter)}
                className='w-[100%] flex justify-between px-4 py-2 bg-[#e0e0e04b] rounded-t-[20px] mt-2 cursor-pointer'>
                <div className='flex items-center text-[#576071] font-semibold gap-3 dark:text-[#fff]'>
                    <RiCollapseDiagonalLine /> Filter Records
                </div>
                <div className='flex items-center text-[#576071] font-semibold gap-3 dark:text-[#fff]'>
                    {showfilter ? <IoIosArrowDown /> : <IoIosArrowUp />}
                </div>
            </div>

            {showfilter && (
                <div className='bg-[#edf1f5] py-2 px-4 w-[100%] dark:bg-[#121519]'>
                    <div className='w-[100%] flex flex-wrap gap-2'>
                        <div className='w-full sm:w-[48%] lg:w-[19%] flex flex-col gap-1'>
                            <label className='text-[#000] text-[12px] font-semibold dark:text-[#fff]'>Campaign name:</label>
                            <input
                                value={Campaignname}
                                onChange={e => setCampaignname(e.target.value)}
                                className='text-[#454546] border-[#d4cfcf] border rounded-md text-[12px] font-semibold px-2 py-2 outline-none dark:bg-[#121519] dark:text-[#fff]'
                                type="text" placeholder='Search...'
                            />
                        </div>
                        <div className='w-full sm:w-[48%] lg:w-[19%] flex flex-col gap-1'>
                            <label className='text-[#000] text-[12px] font-semibold dark:text-[#fff]'>Template Name:</label>
                            <input
                                value={TemplateName}
                                onChange={e => setTemplateName(e.target.value)}
                                className='text-[#454546] border-[#d4cfcf] border rounded-md text-[12px] font-semibold px-2 py-2 outline-none dark:bg-[#121519] dark:text-[#fff]'
                                type="text" placeholder='Search...'
                            />
                        </div>
                        <div className='w-full sm:w-[48%] lg:w-[19%] flex flex-col gap-1'>
                            <label className='text-[#000] text-[12px] font-semibold dark:text-[#fff]'>Template Type</label>
                            <select value={TemplateType} onChange={e => setTemplateType(e.target.value)}
                                className='text-[#454546] border-[#d4cfcf] border rounded-md text-[12px] font-semibold px-2 py-2 outline-none dark:bg-[#121519] dark:text-[#fff]'>
                                <option value="">Select Template Type</option>
                                <option value="101">Utility</option>
                                <option value="103">Authentication</option>
                                <option value="104">Marketing</option>
                            </select>
                        </div>
                        <div className='w-full sm:w-[48%] lg:w-[19%] flex flex-col gap-1'>
                            <label className='text-[#000] text-[12px] font-semibold dark:text-[#fff]'>Interface</label>
                            <select value={Interface} onChange={e => setInterface(e.target.value)}
                                className='text-[#454546] border-[#d4cfcf] border rounded-md text-[12px] font-semibold px-2 py-2 outline-none dark:bg-[#121519] dark:text-[#fff]'>
                                <option value="">Select Interface</option>
                                <option value="0">None</option>
                                <option value="1">Web</option>
                                <option value="2">Http</option>
                                <option value="3">OTP</option>
                                <option value="4">Excel</option>
                                <option value="5">Scheduler</option>
                                <option value="6">ExcelPlugin</option>
                                <option value="7">SMPP</option>
                            </select>
                        </div>
                        <div className='w-full sm:w-[48%] lg:w-[19%] flex flex-col gap-1'>
                            <label className='text-[#000] text-[12px] font-semibold dark:text-[#fff]'>Is Scheduled</label>
                            <select value={IsScheduled} onChange={e => setIsScheduled(e.target.value)}
                                className='text-[#454546] border-[#d4cfcf] border rounded-md text-[12px] font-semibold px-2 py-2 outline-none dark:bg-[#121519] dark:text-[#fff]'>
                                <option value="">Select Status</option>
                                <option value="false">No</option>
                                <option value="true">Yes</option>
                            </select>
                        </div>
                        <div className='w-full sm:w-[48%] lg:w-[19%] flex items-end gap-1'>
                            <button onClick={applyFilters}
                                className='flex items-center justify-center border border-[#37715d] rounded-lg p-2 text-[#37715d] gap-2 hover:bg-[#37715d] hover:text-white w-full sm:w-auto'>
                                <FaFilter /> <span className='text-[12px] font-bold'>Filter</span>
                            </button>
                            <button onClick={clearFilters}
                                className='flex items-center justify-center border border-[#3f7fca] rounded-lg p-2 text-[#3f7fca] gap-2 hover:bg-[#3f7fca] hover:text-white w-full sm:w-auto'>
                                <TbReload /> <span className='text-[12px] font-bold'>Clear Filter</span>
                            </button>
                            <button onClick={fetchCampaigns}
                                className='flex items-center justify-center border border-[#576071] rounded-lg p-2 text-[#576071] gap-2 hover:bg-[#576071] hover:text-white w-full sm:w-auto'>
                                <TbReload /> <span className='text-[12px] font-bold'>Refresh</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Table ── */}
            <table className="w-full overflow-hidden border border-[#dee2e6] relative">
                <thead>
                    <tr className="text-left text-sm font-semibold text-gray-700 bg-[#f3f4f6]">
                        {[
                            'Campaign Date', 'Campaign Info', 'Template Info',
                            'Interface / Channel', 'Scheduled Info',
                            'Is Journey Campaign', 'Progress', 'Total Count', 'Action'
                        ].map(h => (
                            <th key={h} className="p-1 cursor-pointer border border-[#dee2e6]">
                                <div className="flex justify-between items-center gap-2">
                                    <span className='text-[11px] text-[#576071] dark:text-[#fff]'>{h}</span>
                                    <TbArrowsSort className='text-gray-400' />
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        <tr><td colSpan={9} className="p-6 text-center text-gray-500 text-[13px]">
                            <div className='flex justify-center items-center gap-2'>
                                <div className='animate-spin w-5 h-5 border-4 border-[#ed7b00] border-t-transparent rounded-full'></div>
                                Loading campaigns...
                            </div>
                        </td></tr>
                    ) : error ? (
                        <tr><td colSpan={9} className="p-4 text-center text-red-500 text-[13px]">
                            {error}
                            <button onClick={fetchCampaigns} className='ml-2 underline text-[#316bb0]'>Retry</button>
                        </td></tr>
                    ) : pagedCampaigns.length === 0 ? (
                        <tr className="text-center text-gray-500 bg-[#f6f9fc] dark:bg-[#121519]">
                            <td colSpan={9} className="p-4 text-[11px]">No data found</td>
                        </tr>
                    ) : pagedCampaigns.map((c, idx) => (
                        <tr key={c.id}
                            className="text-gray-500 odd:bg-white even:bg-[#edf1f5] dark:odd:bg-[#121519] dark:even:bg-[#1a1f24] hover:bg-[#edf1f5]">

                            {/* Campaign Date */}
                            <td className="p-1 text-[11px] border border-[#dee2e6] dark:text-[#fff] whitespace-nowrap">
                                {formatDate(c.created_at)}
                            </td>

                            {/* Campaign Info */}
                            <td className="p-1 text-[11px] border border-[#dee2e6] dark:text-[#fff]">
                                <div className='font-semibold text-[#374151] dark:text-[#fff]'>
                                    {c.campaign_name || `Campaign #${c.id}`}
                                </div>
                                <div className='text-[10px] text-gray-400'>ID: {c.id}</div>
                            </td>

                            {/* Template Info */}
                            <td className="p-1 text-[11px] border border-[#dee2e6]">
                                <div className='font-semibold text-[#316bb0]'>{c.template_name || '—'}</div>
                                <div className='text-[10px] text-gray-400'>{c.language || '—'}</div>
                            </td>

                            {/* Interface / Channel */}
                            <td className="p-1 text-[11px] border border-[#dee2e6] text-gray-500 font-mono">
                                <div>{c.phone_number_id || '—'}</div>
                                <div className='text-[10px] text-gray-400'>Web</div>
                            </td>

                            {/* Scheduled Info */}
                            <td className="p-1 text-[11px] border border-[#dee2e6] text-gray-500">
                                {c.scheduled_at ? (
                                    <span className='bg-[#fffbe6] text-[#856404] text-[10px] px-1.5 py-0.5 rounded font-semibold'>
                                        🕒 {formatDate(c.scheduled_at)}
                                    </span>
                                ) : (
                                    <span className='text-[10px] text-gray-400'>Not Scheduled</span>
                                )}
                            </td>

                            {/* Is Journey Campaign */}
                            <td className="p-1 text-[11px] border border-[#dee2e6] text-center">
                                <span className='text-[10px] text-gray-400'>—</span>
                            </td>

                            {/* Progress */}
                            <td className="p-1 border border-[#dee2e6]">
                                <MiniProgress
                                    sent={c.sent_count    || 0}
                                    failed={c.failed_count || 0}
                                    total={c.total_numbers || 0}
                                />
                                <div className='mt-1'>
                                    <StatusBadge status={c.status} />
                                </div>
                            </td>

                            {/* Total Count */}
                            <td className="p-1 text-[11px] border border-[#dee2e6] text-center font-bold text-[#374151] dark:text-[#fff]">
                                {c.total_count || 0}
                            </td>

                            {/* Action */}
                            <td className="p-1 text-[11px] border border-[#dee2e6]">
                                <button
                                    onClick={() => setSelectedCampaign(c)}
                                    className='px-2 py-1 bg-[#316bb0] text-white rounded text-[11px] font-bold hover:bg-[#254f8a]'
                                >
                                    View
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* ── Pagination ── */}
            <div className='flex justify-between w-[100%] mt-2 flex-wrap gap-2'>
                <p className='text-[11px] text-[#3f7fc8]'>
                    Showing {filtered.length === 0 ? 0 : indexoffirstuser + 1} to {Math.min(indexoflastuser, filtered.length)} of {filtered.length} records
                </p>
                <div className="flex items-center space-x-2">
                    <button
                        disabled={currentPage === 1}
                        onClick={() => setcurrentPage(1)}
                        className="px-2 py-1 bg-gray-200 rounded disabled:opacity-40 hover:bg-gray-300 text-[11px] font-bold"
                    >{'<<'}</button>
                    <button
                        disabled={currentPage === 1}
                        onClick={() => setcurrentPage(p => p - 1)}
                        className="px-2 py-1 bg-gray-200 rounded disabled:opacity-40 hover:bg-gray-300 text-[11px]"
                    >Prev</button>

                    {Array.from({ length: Math.min(5, totalpages) }, (_, i) => {
                        const start = Math.max(1, Math.min(currentPage - 2, totalpages - 4));
                        return start + i;
                    }).filter(p => p <= totalpages).map(p => (
                        <button key={p} onClick={() => setcurrentPage(p)}
                            className={`px-2 py-1 rounded text-[11px] font-bold border ${p === currentPage ? 'bg-[#448c74] text-white border-[#448c74]' : 'bg-gray-200 border-gray-200 hover:bg-gray-300'}`}>
                            {p}
                        </button>
                    ))}

                    <button
                        disabled={currentPage === totalpages}
                        onClick={() => setcurrentPage(p => p + 1)}
                        className="px-2 py-1 bg-gray-200 rounded disabled:opacity-40 hover:bg-gray-300 text-[11px]"
                    >Next</button>
                    <button
                        disabled={currentPage === totalpages}
                        onClick={() => setcurrentPage(totalpages)}
                        className="px-2 py-1 bg-gray-200 rounded disabled:opacity-40 hover:bg-gray-300 text-[11px] font-bold"
                    >{'>>'}</button>

                    <p className='text-[11px] text-[#3f7fc8]'>
                        Page {currentPage} of {totalpages}
                    </p>
                </div>
            </div>
        </>
    );
}

export default WhatsAppCampaignReport;